import { ComponentType, useState } from 'react';
import { makeRequest } from '../service/makeRequest';
import { ExistingUser } from '../types/ExistingUser';
import { Chip } from './Chip';

type Props = {
  draftUser: ExistingUser | undefined;
  onDraftUserUpdated: <T extends keyof ExistingUser>(
    property: T,
    value: ExistingUser[T]
  ) => void;
  onEdit: () => void;
  onRevert: () => void;
  onUserDeleted: () => void;
  onUserSaved: (savedUser: ExistingUser) => void;
  user: ExistingUser;
};

export const ExistingUserRow: ComponentType<Props> = ({
  draftUser,
  onDraftUserUpdated,
  onEdit,
  onRevert,
  onUserDeleted,
  onUserSaved,
  user,
}) => {
  const isEdited = draftUser?.id_user === user.id_user;
  const [isLoading, setLoading] = useState(false);

  return (
    <tr>
      <th>{user.id_user}</th>
      <td>
        {isEdited ? (
          <>
            <input
              disabled={isLoading}
              onChange={({ target: { value } }) =>
                onDraftUserUpdated('username', value)
              }
              value={draftUser.username}
            />
            {(draftUser.username.length === 0 && <Chip intent="missing" />) ||
              (draftUser.username !== user.username && (
                <Chip intent="unsaved" />
              ))}
          </>
        ) : (
          user.username
        )}
      </td>
      <td>
        {isEdited ? (
          <>
            <input
              disabled={isLoading}
              onChange={({ target: { value } }) =>
                onDraftUserUpdated(
                  'password',
                  value.length > 0 ? value : undefined
                )
              }
              value={draftUser.password || ''}
            />
            {draftUser.password !== undefined &&
              draftUser.password !== user.password && <Chip intent="unsaved" />}
          </>
        ) : (
          <i>hidden</i>
        )}
      </td>
      <td>
        <input
          disabled={!isEdited || isLoading}
          type="checkbox"
          checked={isEdited ? draftUser.super : user.super}
          onChange={
            isEdited
              ? () => onDraftUserUpdated('super', !draftUser.super)
              : undefined
          }
        />
        {isEdited && draftUser.super !== user.super && 'Unsaved changes'}
      </td>
      <td>
        {isEdited ? (
          <>
            <button disabled={isLoading} onClick={onRevert}>
              Revert
            </button>
            <button
              disabled={isLoading}
              onClick={() =>
                makeRequest(
                  'PATCH',
                  `/users/${user.id_user}`,
                  draftUser,
                  setLoading,
                  onUserSaved
                )
              }
            >
              Save
            </button>
          </>
        ) : (
          <>
            <button disabled={isLoading} onClick={onEdit}>
              Edit
            </button>
            <button
              disabled={isLoading}
              onClick={() => {
                if (
                  !window.confirm(
                    `Are you sure you want to delete ${user.username}?\n\nThis cannot be undone.`
                  )
                ) {
                  return;
                }

                makeRequest(
                  'DELETE',
                  `/users/${user.id_user}`,
                  undefined,
                  setLoading,
                  onUserDeleted
                );
              }}
            >
              Delete
            </button>
          </>
        )}
      </td>
    </tr>
  );
};
